import { Box, styled, Tab, TabProps, Tabs } from '@mui/material';
import { Stack } from 'components/stack';
import { Tooltip } from 'components/tooltip';
import React, { useEffect, useState } from 'react';

const exampleData = [
  { id: 1, name: ' A Visit to daughter 1', primaryMetric: false },
  { id: 2, name: 'B Visit to daughter 2', primaryMetric: false },
  { id: 3, name: 'C Visit to daughter 3', primaryMetric: false },
  { id: 4, name: 'Visit to daughter 4', primaryMetric: false },
  { id: 5, name: 'Visit to daughter 5', primaryMetric: false },
  { id: 6, name: 'Visit to daughter 6', primaryMetric: false },
  { id: 7, name: 'AA Visit to daughter 7', primaryMetric: true },
  { id: 8, name: 'Visit to daughter 8', primaryMetric: false }
];

const ScrollContainer = styled(Box)({
  position: 'relative',
  width: '100%',
  maxWidth: '855px',
  backgroundColor: 'transparent',
  display: 'flex',
  verticalAlign: 'center',
  justifyContent: 'space-between',
  '&:after': {
    content: '""',
    position: 'absolute',
    width: '100%',
    height: '2px',
    background: '#f1f5f9',
    bottom: 0
  }
});

const ScrollButtons = styled(Box)({
  borderBottom: '2px solid #f1f5f9',
  paddingLeft: '2px',
  right: 0,
  display: 'flex',
  gap: '2px',
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  zIndex: 1,
  '&:before': {
    content: '""',
    background: '#CBD4E1',
    width: '1px',
    height: '26px',
    margin: 'auto'
  }
});

const StyledTabs = styled(Tabs)({
  '& .MuiTabs-root, & .MuiTabs-scroller': {
    border: 'none',
    overflow: 'auto !important',
    '&::-webkit-scrollbar': {
      display: 'none'
    }
  },
  '& .MuiTab-root': {
    maxWidth: 'none',
    minWidth: 'auto'
  }
});

const StyledTab = styled(({ active, ...props }: { active: boolean } & TabProps) => (
  <Tab {...props} />
))(({ active }: { active: boolean }) => ({
  maxHeight: '48px',
  textTransform: 'none',
  backgroundColor: active ? '#f8fafc' : 'transparent',
  color: active ? '#188aa1 !important' : '#8da0b9 !important',
  fontWeight: active ? 'bold' : 'normal',
  fontSize: '16px',
  padding: '16px 12px',
  transition: 'transform 0.3s ease-in-out',
  '&:hover': {
    backgroundColor: active ? '#f8fafc' : 'rgba(0, 0, 0, 0.04)'
  },
  '&.Mui-selected': {
    color: '#188aa1 !important'
  }
}));

function TabPanel({ active, index, children }: { active: boolean; index: number; children: any }) {
  return (
    <div
      role="tabpanel"
      style={{
        display: active ? 'flex' : 'none'
      }}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
    >
      <div
        style={{
          marginTop: '16px',
          width: '100%'
        }}
      >
        {children}
      </div>
    </div>
  );
}

const sortByStarredAndLabel = (
  array: Array<{ name: string; primaryMetric: boolean; id: number }>
) => {
  return [...array].sort((a, b) => {
    if (b.primaryMetric !== a.primaryMetric) {
      return b.primaryMetric ? 1 : -1;
    }
    return a.name.localeCompare(b.name);
  });
};

type TabsHeaderData = {
  name: string;
  primaryMetric: boolean;
  id: number;
};
type ScrollableTabsProp = {
  children?: JSX.Element;
  list?: TabsHeaderData[];
  setPrimaryMetric?: (e: any) => void;
  getMetricId?: (e: any) => void;
};

const ScrollableTabs = ({
  children,
  list = exampleData,
  setPrimaryMetric,
  getMetricId
}: ScrollableTabsProp) => {
  const [activeTab, setActiveTab] = useState(0);
  const [tabHeaders, setTabHeaders] = useState(sortByStarredAndLabel(list));
  const [showScrollButtons, setShowScrollButtons] = useState(false);
  const [isLeftDisabled, setIsLeftDisabled] = useState(true);
  const [isRightDisabled, setIsRightDisabled] = useState(true);

  useEffect(() => {
    const newTabHeaders = sortByStarredAndLabel(list);
    setTabHeaders(newTabHeaders);
  }, [list]);

  useEffect(() => {
    if (getMetricId) {
      if (!tabHeaders[activeTab]) {
        if (tabHeaders.length === 0) {
          setActiveTab(0);
          getMetricId(null);
        } else {
          const lastValidIndex = tabHeaders.length - 1;
          setActiveTab(lastValidIndex);
          getMetricId(tabHeaders[lastValidIndex]?.id || null);
        }
      } else {
        getMetricId(tabHeaders[activeTab].id);
      }
    }
  }, [activeTab, tabHeaders, getMetricId]);

  const tabsRef = React.useRef<HTMLDivElement | HTMLButtonElement | null | any>();

  useEffect(() => {
    const checkOverflow = () => {
      if (tabsRef.current) {
        const container = tabsRef.current;
        const scroller = container.querySelector('.MuiTabs-scroller');
        if (scroller) {
          const scrollLeft = scroller.scrollLeft;
          const maxScrollLeft = scroller.scrollWidth - scroller.clientWidth;

          setShowScrollButtons(scroller.scrollWidth > container.clientWidth);
          setIsLeftDisabled(scrollLeft <= 0);
          setIsRightDisabled(scrollLeft >= maxScrollLeft);
        }
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => {
      window.removeEventListener('resize', checkOverflow);
    };
  }, [tabHeaders]);

  const handleTabChange = (event: unknown, newValue: React.SetStateAction<number>) => {
    setActiveTab(newValue);
  };

  const handleStarClick = (index: number, event: { stopPropagation: () => void }) => {
    event.stopPropagation();

    setTabHeaders((prevTabs) => {
      const activeTabId = prevTabs[index].id;

      const updatedTabs = prevTabs.map((tab, i) => ({
        ...tab,
        primaryMetric: i === index ? !tab.primaryMetric : false
      }));
      const sortedTabs = sortByStarredAndLabel(updatedTabs);
      const newActiveIndex = sortedTabs.findIndex((tab) => tab.id === activeTabId);
      setActiveTab(newActiveIndex);

      if (setPrimaryMetric) {
        setPrimaryMetric({ metricId: activeTabId, type: updatedTabs[index].primaryMetric });
      }

      return sortedTabs;
    });
  };

  const handleScroll = (direction: string) => {
    if (tabsRef.current) {
      const container = tabsRef.current.querySelector('.MuiTabs-scroller');
      if (container) {
        const scrollAmount = 200;
        container.scrollBy({
          left: direction === 'left' ? -scrollAmount : scrollAmount,
          behavior: 'smooth'
        });

        // Update button states after scroll
        const scrollLeft =
          container.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
        const maxScrollLeft = container.scrollWidth - container.clientWidth;
        setIsLeftDisabled(scrollLeft <= 0);
        setIsRightDisabled(scrollLeft >= maxScrollLeft);
      }
    }
  };

  return (
    <div style={{ marginTop: '16px' }}>
      <ScrollContainer>
        <StyledTabs
          ref={tabsRef}
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons={false}
          aria-label="scrollable tabs"
          sx={{
            '& .MuiTabs-indicator': {
              backgroundColor: '#188AA1',
              zIndex: 2
            }
          }}
        >
          {tabHeaders.map((tab, index) => (
            <StyledTab
              key={index}
              active={activeTab === index}
              label={
                <Stack sx={{ display: 'flex', alignItems: 'center', gap: 1, flexDirection: 'row' }}>
                  <Tooltip
                    placement="bottom"
                    toolTipText={
                      <Stack width="333px" display="flex" gap="8px" padding="4px">
                        <p style={{ fontSize: '14px' }}>
                          The Primary Metric is the most important and will always show up as the
                          first tab.
                        </p>
                      </Stack>
                    }
                  >
                    <span
                      className="material-icons"
                      onClick={(e: React.MouseEvent<HTMLSpanElement>) =>
                        activeTab === index && handleStarClick(index, e)
                      }
                      style={{
                        cursor: 'pointer',
                        fontSize: '20px',
                        display: 'flex',
                        color: activeTab === index ? '#188aa1' : '#ccc'
                      }}
                    >
                      {tab.primaryMetric ? 'star' : 'star_outline'}
                    </span>
                  </Tooltip>
                  {tab.name.length > 34 && activeTab !== index ? (
                    <Tooltip blackTooltip placement="top-start" toolTipText={tab.name}>
                      <p
                        style={{
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '350px'
                        }}
                      >
                        {tab.name}
                      </p>
                    </Tooltip>
                  ) : (
                    <p
                      style={{
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: activeTab === index ? 'auto' : '350px'
                      }}
                    >
                      {tab.name}
                    </p>
                  )}
                </Stack>
              }
              aria-label={`${tab.name} tab`}
            />
          ))}
        </StyledTabs>
        {showScrollButtons && (
          <ScrollButtons>
            <div
              onClick={() => !isLeftDisabled && handleScroll('left')}
              aria-label="scroll left"
              style={{
                margin: 'auto',
                pointerEvents: isLeftDisabled ? 'none' : 'auto'
              }}
            >
              <span
                className="material-icons"
                style={{
                  color: isLeftDisabled ? '#CBD4E1' : '#188aa1',
                  fontSize: '20px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: 'pointer',
                  userSelect: 'none',
                  padding: '4px'
                }}
              >
                chevron_left
              </span>
            </div>
            <div
              onClick={() => !isRightDisabled && handleScroll('right')}
              aria-label="scroll right"
              style={{
                margin: 'auto',
                pointerEvents: isRightDisabled ? 'none' : 'auto'
              }}
            >
              <span
                className="material-icons"
                style={{
                  color: isRightDisabled ? '#CBD4E1' : '#188aa1',
                  fontSize: '20px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: 'pointer',
                  userSelect: 'none',
                  padding: '4px'
                }}
              >
                chevron_right
              </span>
            </div>
          </ScrollButtons>
        )}
      </ScrollContainer>
      {tabHeaders.map((tab, index) => (
        <TabPanel key={index} active={activeTab === index} index={index}>
          {children}
        </TabPanel>
      ))}
    </div>
  );
};

export default ScrollableTabs;
